import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  UserRole, 
  AdminPermission, 
  UserProfile, 
  hasPermission, 
  isAdminRole, 
  canManageUser,
  ROLE_CONFIGURATIONS 
} from '../types/userTypes';

class RoleService {
  /**
   * Get user's role and permissions
   */
  async getUserRole(userId: string): Promise<{ role: UserRole; permissions: AdminPermission[]; customPermissions?: AdminPermission[] }> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return { role: 'user', permissions: [] };
      }

      const userData = userDoc.data() as UserProfile;
      
      // Handle legacy isAdmin field
      let role: UserRole = userData.role || 'user';
      if (!userData.role && userData.isAdmin) {
        role = 'system_admin'; // Convert legacy admin to system admin
      }

      const roleConfig = ROLE_CONFIGURATIONS[role];
      const customPermissions = userData.adminPermissions || [];
      
      // Combine role permissions with custom permissions
      const allPermissions = [
        ...roleConfig.permissions,
        ...customPermissions
      ];
      
      // Remove duplicates
      const uniquePermissions = Array.from(new Set(allPermissions));

      return {
        role,
        permissions: uniquePermissions,
        customPermissions: customPermissions.length > 0 ? customPermissions : undefined
      };
    } catch (error) {
      console.error('Error getting user role:', error);
      return { role: 'user', permissions: [] };
    }
  }

  /**
   * Check if user has specific permission
   */
  async checkPermission(userId: string, permission: AdminPermission): Promise<boolean> {
    try {
      const { role, customPermissions } = await this.getUserRole(userId);
      return hasPermission(role, permission, customPermissions);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  /**
   * Update user's role
   */
  async updateUserRole(
    userId: string, 
    newRole: UserRole, 
    adminUserId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Check if admin has permission to manage users
      const adminRole = await this.getUserRole(adminUserId);
      if (!hasPermission(adminRole.role, 'user_management', adminRole.customPermissions)) {
        return { success: false, error: 'Insufficient permissions to manage users' };
      }

      // Get target user's current role
      const targetUserDoc = await getDoc(doc(db, 'users', userId));
      if (!targetUserDoc.exists()) {
        return { success: false, error: 'User not found' };
      }

      const targetUserData = targetUserDoc.data() as UserProfile;
      const currentRole = targetUserData.role || 'user';

      // Check if admin can manage this user
      if (!canManageUser(adminRole.role, currentRole)) {
        return { success: false, error: 'Cannot manage user with equal or higher role' };
      }

      // Check if admin can assign the new role
      if (newRole !== 'user' && !canManageUser(adminRole.role, newRole)) {
        return { success: false, error: 'Cannot assign role with equal or higher permissions' };
      }

      // Update the user's role
      const updateData: any = {
        role: newRole,
        isAdmin: isAdminRole(newRole), // Update legacy field for backwards compatibility
        updatedAt: serverTimestamp(),
        roleUpdatedBy: adminUserId,
        roleUpdatedAt: serverTimestamp(),
      };

      if (reason) {
        updateData.roleUpdateReason = reason;
      }

      await updateDoc(doc(db, 'users', userId), updateData);

      // Log the role change
      await this.logRoleChange(userId, currentRole, newRole, adminUserId, reason);

      return { success: true };
    } catch (error) {
      console.error('Error updating user role:', error);
      return { success: false, error: 'Failed to update user role' };
    }
  }

  /**
   * Grant custom permission to user
   */
  async grantCustomPermission(
    userId: string,
    permission: AdminPermission,
    adminUserId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Check admin permissions
      const adminRole = await this.getUserRole(adminUserId);
      if (!hasPermission(adminRole.role, 'user_management', adminRole.customPermissions)) {
        return { success: false, error: 'Insufficient permissions' };
      }

      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return { success: false, error: 'User not found' };
      }

      const userData = userDoc.data() as UserProfile;
      const currentPermissions = userData.adminPermissions || [];

      if (currentPermissions.includes(permission)) {
        return { success: false, error: 'Permission already granted' };
      }

      const updatedPermissions = [...currentPermissions, permission];

      await updateDoc(doc(db, 'users', userId), {
        adminPermissions: updatedPermissions,
        updatedAt: serverTimestamp(),
        permissionUpdatedBy: adminUserId,
        permissionUpdatedAt: serverTimestamp(),
        permissionUpdateReason: reason || 'Custom permission granted',
      });

      return { success: true };
    } catch (error) {
      console.error('Error granting permission:', error);
      return { success: false, error: 'Failed to grant permission' };
    }
  }

  /**
   * Revoke custom permission from user
   */
  async revokeCustomPermission(
    userId: string,
    permission: AdminPermission,
    adminUserId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Check admin permissions
      const adminRole = await this.getUserRole(adminUserId);
      if (!hasPermission(adminRole.role, 'user_management', adminRole.customPermissions)) {
        return { success: false, error: 'Insufficient permissions' };
      }

      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return { success: false, error: 'User not found' };
      }

      const userData = userDoc.data() as UserProfile;
      const currentPermissions = userData.adminPermissions || [];

      if (!currentPermissions.includes(permission)) {
        return { success: false, error: 'Permission not found' };
      }

      const updatedPermissions = currentPermissions.filter(p => p !== permission);

      await updateDoc(doc(db, 'users', userId), {
        adminPermissions: updatedPermissions,
        updatedAt: serverTimestamp(),
        permissionUpdatedBy: adminUserId,
        permissionUpdatedAt: serverTimestamp(),
        permissionUpdateReason: reason || 'Custom permission revoked',
      });

      return { success: true };
    } catch (error) {
      console.error('Error revoking permission:', error);
      return { success: false, error: 'Failed to revoke permission' };
    }
  }

  /**
   * Get all users with admin roles
   */
  async getAdminUsers(): Promise<UserProfile[]> {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('role', 'in', ['system_admin', 'community_admin', 'signals_admin'])
      );
      
      const querySnapshot = await getDocs(usersQuery);
      const adminUsers: UserProfile[] = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data() as UserProfile;
        userData.uid = doc.id;
        adminUsers.push(userData);
      });

      // Also get legacy admin users (where isAdmin is true but role is not set)
      const legacyAdminQuery = query(
        collection(db, 'users'),
        where('isAdmin', '==', true)
      );

      const legacySnapshot = await getDocs(legacyAdminQuery);
      legacySnapshot.forEach((doc) => {
        const userData = doc.data() as UserProfile;
        userData.uid = doc.id;
        
        // Only add if not already in the list and doesn't have a role set
        if (!userData.role && !adminUsers.find(u => u.uid === userData.uid)) {
          userData.role = 'system_admin'; // Treat legacy admins as system admins
          adminUsers.push(userData);
        }
      });

      return adminUsers;
    } catch (error) {
      console.error('Error getting admin users:', error);
      return [];
    }
  }

  /**
   * Log role changes for audit purposes
   */
  private async logRoleChange(
    userId: string,
    oldRole: UserRole,
    newRole: UserRole,
    adminUserId: string,
    reason?: string
  ): Promise<void> {
    try {
      // You can implement a separate audit log collection here
      console.log('Role change logged:', {
        userId,
        oldRole,
        newRole,
        adminUserId,
        reason,
        timestamp: new Date(),
      });
    } catch (error) {
      console.error('Error logging role change:', error);
    }
  }

  /**
   * Migrate legacy admin users to new role system
   */
  async migrateLegacyAdmins(): Promise<{ success: boolean; migratedCount: number; error?: string }> {
    try {
      const legacyAdminQuery = query(
        collection(db, 'users'),
        where('isAdmin', '==', true)
      );

      const querySnapshot = await getDocs(legacyAdminQuery);
      let migratedCount = 0;

      const promises = querySnapshot.docs.map(async (docSnapshot) => {
        const userData = docSnapshot.data() as UserProfile;
        
        // Only migrate if role is not already set
        if (!userData.role) {
          await updateDoc(doc(db, 'users', docSnapshot.id), {
            role: 'system_admin',
            updatedAt: serverTimestamp(),
            migrationNote: 'Migrated from legacy isAdmin field',
          });
          migratedCount++;
        }
      });

      await Promise.all(promises);

      return { success: true, migratedCount };
    } catch (error) {
      console.error('Error migrating legacy admins:', error);
      return { success: false, migratedCount: 0, error: 'Migration failed' };
    }
  }
}

export default new RoleService(); 